/**
 * EBIKO Fallback Sale Pill Injector
 * ─────────────────────────────────
 * Cards in product-item / ebk-sbc / ebk-md sections render a default discount
 * badge (.product-label--on-sale, .ebk-sbc__badge, etc.) when a variant has
 * compare_at_price > price. The site-wide CSS in layout/theme.liquid hides
 * those default badges so the layout stays clean for ODA branding.
 *
 * The main universal injector (ebk-promo-pill-injector.js) replaces the hidden
 * badge with a branded ODA pill — but only on cards that have an EBIKO rule.
 * Cards WITHOUT a rule end up with no badge at all, even when on sale.
 *
 * This script fixes that by injecting a green ODA-styled fallback pill on
 * every card that:
 *   1. Has a hidden default discount badge in the DOM
 *   2. Does NOT already have an EBIKO promo pill
 *
 * Result: every discounted card shows ONE branded pill — either the specific
 * ODA rule (when present) or a fallback "Save X%" pill (when not).
 *
 * Loaded via layout/theme.liquid AFTER ebk-promo-pill-injector.js so the main
 * injector runs first and we only fill in the gaps it leaves.
 */
(function(){
  if (window.__ebkFallbackSalePillInit__) return;
  window.__ebkFallbackSalePillInit__ = true;

  function makePill(text){
    var p = document.createElement('span');
    // ⚠️ DO NOT set data-ebk-promo-pill on fallback pills.
    // The main injector at ebk-promo-pill-injector.js line 174 skips cards
    // that already have [data-ebk-promo-pill] — claiming that attribute
    // would block the real ODA pill from ever rendering.
    // Use a dedicated marker so main injector and this fallback can coexist.
    p.setAttribute('data-ebk-fallback-pill', '1');
    p.style.cssText = [
      'position:absolute','top:6px','left:6px','z-index:1',
      'display:inline-flex','align-items:center','gap:4px',
      'padding:4px 9px','border-radius:14px',
      'background:#10b981','color:#fff',
      'font-size:10.5px','font-weight:700','letter-spacing:.3px',
      'line-height:1.2','white-space:nowrap',
      'box-shadow:0 2px 6px rgba(0,0,0,.15)','pointer-events:none'
    ].join(';');
    p.textContent = text;
    return p;
  }

  function pickAnchor(card){
    // Anchor pill on the product image. Walk up to the image's parent so the
    // pill positions absolutely against the image container.
    var img = card.querySelector('img');
    if (!img) return null;
    var anchor = img.parentElement || card;
    var cs = window.getComputedStyle(anchor);
    if (cs.position === 'static') anchor.style.position = 'relative';
    return anchor;
  }

  function normalizeText(raw){
    var t = (raw || '').replace(/\s+/g,' ').trim();
    if (!t) return null;
    // Bare percent like "23%" → "Save 23%"
    if (/^-?\d+(?:\.\d+)?\s*%$/.test(t)) {
      return 'Save ' + t.replace(/\s+/g,'');
    }
    // Already "Save 13%" / "13% off" / etc — keep as is
    return t;
  }

  function parsePrice(s){
    if (!s) return NaN;
    var m = String(s).replace(/[\s ]/g,'').match(/(-?\d+(?:[.,]\d+)?)/);
    if (!m) return NaN;
    return parseFloat(m[1].replace(',', '.'));
  }

  // Detect a sale on a card by looking at strikethrough/compare price elements.
  // Universal signal — works for product-item.liquid, ebk-sbc, AJAX-loaded
  // recommendations, and anything else with a "$current ~~$original~~" layout.
  // Returns a "Save X%" string or null.
  function detectSaleByPrice(card){
    if (!card) return null;
    // 1. Compare/regular price element
    var compareEl = card.querySelector(
      's, del, [class*="compare"], [class*="--regular"], .price__compare, .price-item--regular'
    );
    if (!compareEl) return null;
    var compareTxt = compareEl.textContent || '';
    if (!/\$/.test(compareTxt)) return null;
    var compare = parsePrice(compareTxt);
    if (!isFinite(compare) || compare <= 0) return null;
    // 2. Current/sale price element
    var currentEl = card.querySelector(
      '[class*="--sale"], [class*="--current"], .price-item--sale, .price__current, ' +
      '.product-item__price, .price--on-sale, .price'
    );
    var current = NaN;
    if (currentEl) {
      // Some price wrappers contain BOTH current and compare; take the first $.. that
      // isn't inside the compareEl.
      var clone = currentEl.cloneNode(true);
      var nestedCompares = clone.querySelectorAll('s, del, [class*="compare"], [class*="--regular"]');
      nestedCompares.forEach(function(n){ n.remove(); });
      current = parsePrice(clone.textContent);
    }
    if (!isFinite(current) || current <= 0 || current >= compare) return null;
    var pct = Math.round((compare - current) * 100 / compare);
    if (!pct || pct < 1) return null;
    return 'Save ' + pct + '%';
  }

  function processCard(card){
    if (!card) return;

    // Badge rule #1 (CLAUDE.md “Badge Stacking Rules”): condition-owned zones (clearance
    // bucket/expiry chips) opt out of ALL injected pills via
    // data-ebk-badges="self". Class fallbacks cover cached pre-contract HTML.
    if (card.closest && card.closest('[data-ebk-badges="self"], .ebkc-wrap')) return;

    // Skip if main injector already placed a REAL ODA pill —
    // its specific messaging ("Buy 10 @ $X", "Buy 10, get free gift", etc.)
    // takes priority over our generic "Save X%".
    if (card.querySelector('[data-ebk-promo-pill]:not([data-ebk-fallback-pill])')) return;

    // Skip if we already added our own fallback pill (idempotent).
    if (card.querySelector('[data-ebk-fallback-pill]')) return;

    // Try multiple signal sources — first hit wins.
    //   1. Hidden default badge (rendered by Liquid; most reliable when present)
    //   2. Strikethrough/compare price (works for AJAX-loaded recommendations,
    //      sections that don't render the Liquid badge, etc.)
    var text = null;
    var hiddenBadge = card.querySelector(
      '.product-label--on-sale, .ebk-sbc__badge, .ebk-md__badge, ' +
      '.ebk-product-card__badge, .ebk-category-grid__badge'
    );
    if (hiddenBadge) text = normalizeText(hiddenBadge.textContent);
    if (!text) text = detectSaleByPrice(card);
    if (!text) return;

    var anchor = pickAnchor(card);
    if (!anchor) return;

    var pill = makePill(text);
    // Badge rule #3 (CLAUDE.md “Badge Stacking Rules”): never cover a badge the card renders
    // itself (merchant labels, brand-hub badges…). Anything visible in the
    // anchor's top-left strip keeps the corner; our pill stacks BELOW it, in the
    // badge's OWN frame when possible so left edges align (see the promo
    // injector for the full rationale).
    var stackAfter = null, stackBottom = 0;
    try {
      var aRect = anchor.getBoundingClientRect();
      var own = card.querySelectorAll('[class*="__badge"], [class*="badge--"], [class*="-badge"], .product-label');
      for (var q = 0; q < own.length; q++) {
        var oEl = own[q];
        if (oEl.getAttribute('data-ebk-promo-pill') || oEl.getAttribute('data-ebk-fallback-pill')) continue;
        var r = oEl.getBoundingClientRect();
        if (!r.width || !r.height) continue;                       // hidden badge — ignore
        var relTop = r.top - aRect.top, relLeft = r.left - aRect.left;
        var bottomRel = r.bottom - aRect.top;
        if (relTop > 56 || relLeft > 150) continue;      // starts too low / too far right
        if (bottomRel <= 0 || bottomRel > 140) continue; // doesn't reach the anchor / not a corner chip
        if (r.right - aRect.left <= 0) continue;         // entirely left of the anchor
        if (bottomRel > stackBottom) { stackBottom = bottomRel; stackAfter = oEl; }
      }
    } catch (e) {}
    if (stackAfter && stackAfter.offsetParent && card.contains(stackAfter.offsetParent)) {
      var host = stackAfter.offsetParent;
      pill.style.left = stackAfter.offsetLeft + 'px';
      pill.style.top = (stackAfter.offsetTop + stackAfter.offsetHeight + 6) + 'px';
      host.appendChild(pill);
    } else if (stackAfter) {
      pill.style.top = Math.round(stackBottom + 5) + 'px';
      anchor.appendChild(pill);
    } else {
      anchor.appendChild(pill);
    }
  }

  function inject(){
    // ── Cleanup pass: yield to the real ODA pill ──
    document.querySelectorAll('[data-ebk-fallback-pill]').forEach(function(fp){
      var card = fp.closest(
        '.product-item, .ebk-sbc__card, .ebk-md__card, .ebk-product-card, ' +
        '.ebk-category-grid__item, article, li, .card, .swiper-slide'
      ) || fp.parentElement;
      if (!card) return;
      var realPill = card.querySelector('[data-ebk-promo-pill]:not([data-ebk-fallback-pill])');
      if (realPill && realPill !== fp) fp.remove();
    });

    // ── Inject pass: process every product card on the page ──
    // We sweep card containers directly (rather than starting from a hidden
    // badge) so cards that never rendered the badge still get a pill if their
    // price layout shows a discount.
    var cards = document.querySelectorAll(
      '.product-item, .ebk-sbc__card, .ebk-md__card, .ebk-product-card, ' +
      '.ebk-category-grid__item'
    );
    cards.forEach(processCard);
  }

  function debounce(fn, ms){ var t; return function(){ clearTimeout(t); t = setTimeout(fn, ms); }; }
  var run = debounce(inject, 200);

  // Run AFTER the main injector finishes its ~250ms debounced pass.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ setTimeout(run, 350); });
  } else {
    setTimeout(run, 350);
  }

  // Re-run on DOM changes so lazy-loaded sections (Recently viewed, You may
  // also like, infinite scroll) get the fallback pill too.
  if (typeof MutationObserver !== 'undefined') {
    try {
      var obs = new MutationObserver(run);
      obs.observe(document.body || document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }
})();
